import React, { useState, useEffect, useContext } from 'react';
import { AdminAuthContext } from '../../context/AdminAuthContext';
import { useSocket } from '../../context/SocketContext';
import statusService from '../../services/statusService';
import * as eventService from '../../services/eventService';
import * as friendsService from '../../services/friendsService';
import { useNavigate } from 'react-router-dom';
import { 
  Activity, Users, Calendar, Circle, Zap, Moon, Coffee, MessageCircle,
  MapPin, Clock, UserCheck, Loader, ChevronRight, CalendarClock, PieChart,
  Star, Heart, Smile, Music, Gamepad2, Film, Book, Dumbbell,
  Sparkles, Flame, Home, Briefcase, GraduationCap, Plane, ShoppingCart
} from 'lucide-react';

const STATUS_ICONS = {
  disponibile: Circle,
  occupato: Zap,
  non_disturbare: Moon,
  afk: Coffee,
  in_pausa: MessageCircle
};

const CUSTOM_STATUS_ICONS = {
  Star, Heart, Smile, Music, Gamepad2, Film, Book, Dumbbell,
  Sparkles, Flame, Users, Home, Briefcase, GraduationCap, Plane, ShoppingCart
};

const STATUS_LABELS = {
  disponibile: 'Disponibile',
  occupato: 'Occupato',
  non_disturbare: 'Non Disturbare',
  afk: 'AFK',
  in_pausa: 'In Pausa',
  custom: 'Personalizzato'
};

const STATUS_COLORS = {
  disponibile: {
    bg: 'bg-green-500/20 dark:bg-green-400/20',
    text: 'text-green-700 dark:text-green-300',
    border: 'border-green-500/30 dark:border-green-400/30'
  },
  occupato: {
    bg: 'bg-orange-500/20 dark:bg-orange-400/20',
    text: 'text-orange-700 dark:text-orange-300',
    border: 'border-orange-500/30 dark:border-orange-400/30'
  },
  non_disturbare: {
    bg: 'bg-red-500/20 dark:bg-red-400/20',
    text: 'text-red-700 dark:text-red-300',
    border: 'border-red-500/30 dark:border-red-400/30'
  },
  afk: {
    bg: 'bg-yellow-500/20 dark:bg-yellow-400/20',
    text: 'text-yellow-700 dark:text-yellow-300',
    border: 'border-yellow-500/30 dark:border-yellow-400/30'
  },
  in_pausa: {
    bg: 'bg-blue-500/20 dark:bg-blue-400/20',
    text: 'text-blue-700 dark:text-blue-300',
    border: 'border-blue-500/30 dark:border-blue-400/30'
  }
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const { admin } = useContext(AdminAuthContext);
  const { on } = useSocket();
  
  const [loading, setLoading] = useState(true);
  const [currentStatus, setCurrentStatus] = useState(null);
  const [stats, setStats] = useState({
    totalFriends: 0,
    activeEvents: 0,
    pendingInvites: 0
  });
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [recentFriends, setRecentFriends] = useState([]);

  useEffect(() => {
    if (admin?.id) {
      loadDashboardData();
    }
  }, [admin]);

  useEffect(() => {
    if (!on) return;

    // Socket listeners con cleanup automatico
    const cleanupStatus = on('statusUpdated', handleStatusUpdate);
    const cleanupEvent = on('eventUpdated', loadUpcomingEvents);
    const cleanupFriend = on('friendUpdated', loadRecentFriends);

    return () => {
      cleanupStatus?.();
      cleanupEvent?.();
      cleanupFriend?.();
    };
  }, [on]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCurrentStatus(),
        loadStats(),
        loadUpcomingEvents(),
        loadRecentFriends()
      ]);
    } catch (error) {
      console.error('Errore caricamento dashboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCurrentStatus = async () => {
    try {
      if (!admin?.id) return;
      const response = await statusService.getCurrentStatus(admin.id);
      console.log('📊 Status response:', response);
      const statusData = response.data || response;
      console.log('📊 Status data:', statusData);
      
      setCurrentStatus(statusData);
    } catch (error) {
      console.error('Errore caricamento stato:', error);
    }
  };

  const loadStats = async () => {
    try {
      if (!admin?.id) return;
      
      // Carica amici
      const friendsRes = await friendsService.getAllFriends(admin.id);
      console.log('📊 Friends response:', friendsRes);
      
      // Carica eventi
      const eventsRes = await eventService.getAllEvents(admin.id);
      console.log('📊 Events response:', eventsRes);
      
      // Carica inviti (con fallback in caso di errore)
      let invitesCount = 0;
      try {
        const invitesRes = await friendsService.getPendingInvites(admin.id);
        console.log('📊 Invites response:', invitesRes);
        invitesCount = invitesRes.data?.length || 0;
      } catch (inviteError) {
        console.warn('⚠️ Errore caricamento inviti pendenti:', inviteError);
      }

      setStats({
        totalFriends: friendsRes.data?.length || 0,
        activeEvents: eventsRes.data?.filter(e => new Date(e.eventDate) >= new Date()).length || 0,
        pendingInvites: invitesCount
      });
    } catch (error) {
      console.error('Errore caricamento statistiche:', error);
    }
  };

  const loadUpcomingEvents = async () => {
    try {
      if (!admin?.id) return;
      const response = await eventService.getAllEvents(admin.id);
      console.log('📅 Events response:', response);
      
      const eventsData = response.data || [];
      const upcoming = eventsData
        .filter(event => new Date(event.eventDate) >= new Date())
        .sort((a, b) => new Date(a.eventDate) - new Date(b.eventDate))
        .slice(0, 3);
      setUpcomingEvents(upcoming);
    } catch (error) {
      console.error('Errore caricamento eventi:', error);
    }
  };

  const loadRecentFriends = async () => {
    try {
      if (!admin?.id) return;
      const response = await friendsService.getAllFriends(admin.id);
      if (response.success) {
        const recent = response.data
          .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
          .slice(0, 4);
        setRecentFriends(recent);
      }
    } catch (error) {
      console.error('Errore caricamento amici:', error);
    }
  };

  const handleStatusUpdate = (data) => {
    setCurrentStatus(data);
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('it-IT', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('it-IT', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <Loader className="w-8 h-8 animate-spin text-light-primary dark:text-dark-primary" />
      </div>
    );
  }

  // Determina lo stato e la label esattamente come in StatusManagement.jsx
  let statusLabel;
  let normalizedStatus;
  let StatusIcon;
  let statusColors;
  let customColor = null;
  
  if (currentStatus?.status === 'custom' && currentStatus?.customStatus) {
    // Stato custom
    statusLabel = currentStatus.customStatus.label;
    normalizedStatus = 'custom';
    
    // Usa l'icona custom se disponibile
    const iconName = currentStatus.customStatus.icon;
    StatusIcon = (iconName && CUSTOM_STATUS_ICONS[iconName]) ? CUSTOM_STATUS_ICONS[iconName] : Star;
    
    // Usa il colore custom
    customColor = currentStatus.customStatus.color || '#EC4899';
    
    // Crea colori dinamici per lo stato custom
    statusColors = {
      bg: 'bg-light-surface-container dark:bg-dark-surface-container',
      text: 'text-light-on-surface dark:text-dark-on-surface',
      border: 'border-light-outline dark:border-dark-outline'
    };
  } else if (currentStatus?.status) {
    // Stato standard: converti trattini in underscore
    normalizedStatus = currentStatus.status.replace(/-/g, '_');
    statusLabel = STATUS_LABELS[normalizedStatus] || normalizedStatus;
    StatusIcon = STATUS_ICONS[normalizedStatus] || Activity;
    statusColors = STATUS_COLORS[normalizedStatus] || STATUS_COLORS.disponibile;
  } else {
    // Fallback default
    normalizedStatus = 'disponibile';
    statusLabel = 'Disponibile';
    StatusIcon = Circle;
    statusColors = STATUS_COLORS.disponibile;
  }
  
  console.log('📊 Dashboard state:', { 
    status: currentStatus?.status, 
    normalizedStatus, 
    statusLabel,
    isCustom: currentStatus?.status === 'custom',
    customStatus: currentStatus?.customStatus
  });

  return (
    <div className="min-h-screen bg-light-surface dark:bg-dark-surface p-4 sm:p-6 lg:p-8">
      <div className="max-w-7xl mx-auto space-y-4 sm:space-y-6">
        {/* Header Dashboard */}
        <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-4 sm:p-6
          animate-fade-in opacity-0"
          style={{animationDelay: '100ms', animationFillMode: 'forwards'}}>
          <div className="flex items-center gap-3">
            <PieChart className="w-8 h-8 sm:w-10 sm:h-10 text-light-primary dark:text-dark-primary" />
            <div>
              <h2 className="text-2xl sm:text-3xl font-bold text-light-on-surface dark:text-dark-on-surface">
                Dashboard del Becco
              </h2>
              <p className="mt-1 text-sm sm:text-base text-light-on-surface-variant dark:text-dark-on-surface-variant">
                Benvenuto, {admin?.nome} {admin?.cognome}
              </p>
            </div>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          {/* Stato Becco Corrente */}
          <div 
            onClick={() => navigate('/stato-becco')}
            className={`${statusColors.bg} ${statusColors.border} border-2 rounded-lg shadow-surface-container p-4 sm:p-6 cursor-pointer
              hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200
              animate-fade-in opacity-0`}
            style={{animationDelay: '200ms', animationFillMode: 'forwards'}}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-light-on-surface-variant dark:text-dark-on-surface-variant mb-1">
                  Stato Becco
                </p>
                <p className={`text-xl sm:text-2xl font-bold ${statusColors.text}`}>
                  {statusLabel}
                </p>
              </div>
              <StatusIcon 
                className={`w-8 h-8 sm:w-10 sm:h-10 ${customColor ? '' : statusColors.text} flex-shrink-0`}
                style={customColor ? { color: customColor } : {}}
              />
            </div>
          </div>

          {/* Eventi Attivi */}
          <div 
            onClick={() => navigate('/gestione-eventi')}
            className="bg-light-primary/20 dark:bg-dark-primary/20 border-2 border-light-primary/30 dark:border-dark-primary/30 
              rounded-lg shadow-surface-container p-4 sm:p-6 cursor-pointer
              hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200
              animate-fade-in opacity-0"
            style={{animationDelay: '300ms', animationFillMode: 'forwards'}}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-light-on-surface-variant dark:text-dark-on-surface-variant mb-1">
                  Eventi Attivi
                </p>
                <p className="text-xl sm:text-2xl font-bold text-light-primary dark:text-dark-primary">
                  {stats.activeEvents}
                </p>
              </div>
              <Calendar className="w-8 h-8 sm:w-10 sm:h-10 text-light-primary dark:text-dark-primary flex-shrink-0" />
            </div>
          </div>

          {/* Amici Totali */}
          <div 
            onClick={() => navigate('/gestione-amici')}
            className="bg-light-primary/20 dark:bg-dark-primary/20 border-2 border-light-primary/30 dark:border-dark-primary/30 
              rounded-lg shadow-surface-container p-4 sm:p-6 cursor-pointer
              hover:shadow-lg hover:scale-[1.02] active:scale-[0.98] transition-all duration-200
              animate-fade-in opacity-0"
            style={{animationDelay: '400ms', animationFillMode: 'forwards'}}
          >
            <div className="flex items-center justify-between">
              <div className="flex-1 min-w-0">
                <p className="text-xs sm:text-sm font-medium text-light-on-surface-variant dark:text-dark-on-surface-variant mb-1">
                  Amici Totali
                </p>
                <p className="text-xl sm:text-2xl font-bold text-light-primary dark:text-dark-primary">
                  {stats.totalFriends}
                </p>
              </div>
              <Users className="w-8 h-8 sm:w-10 sm:h-10 text-light-primary dark:text-dark-primary flex-shrink-0" />
            </div>
          </div>
        </div>

        {/* Sezione Eventi e Amici */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Prossimi Eventi */}
          <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-4 sm:p-6
            animate-fade-in opacity-0"
            style={{animationDelay: '500ms', animationFillMode: 'forwards'}}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-light-on-surface dark:text-dark-on-surface flex items-center gap-2">
                <CalendarClock className="w-5 h-5 sm:w-6 sm:h-6 text-light-primary dark:text-dark-primary" />
                Prossimi Eventi
              </h3>
              {upcomingEvents.length > 0 && (
                <button
                  onClick={() => navigate('/gestione-eventi')}
                  className="text-sm font-medium text-light-primary dark:text-dark-primary hover:underline flex items-center gap-1"
                >
                  Vedi tutti
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>

            {upcomingEvents.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <Calendar className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 text-light-on-surface-variant dark:text-dark-on-surface-variant opacity-30" />
                <p className="text-sm sm:text-base text-light-on-surface-variant dark:text-dark-on-surface-variant">
                  Nessun evento in programma
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {upcomingEvents.map((event) => (
                  <div
                    key={event.id}
                    onClick={() => navigate('/gestione-eventi')}
                    className="bg-light-surface dark:bg-dark-surface rounded-lg p-3 sm:p-4 cursor-pointer
                      hover:bg-light-surface-bright dark:hover:bg-dark-surface-bright transition-colors"
                  >
                    <h4 className="font-semibold text-light-on-surface dark:text-dark-on-surface mb-2">
                      {event.title}
                    </h4>
                    <div className="space-y-1 text-xs sm:text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>{formatDate(event.eventDate)} - {formatTime(event.eventDate)}</span>
                      </div>
                      {event.location && (
                        <div className="flex items-center gap-2">
                          <MapPin className="w-4 h-4" />
                          <span>{event.location}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <UserCheck className="w-4 h-4" />
                        <span>{event.participantCount || event.participants?.length || 0} partecipanti</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Lista Amici */}
          <div className="bg-light-surface-container dark:bg-dark-surface-container rounded-lg shadow-surface-container p-4 sm:p-6
            animate-fade-in opacity-0"
            style={{animationDelay: '600ms', animationFillMode: 'forwards'}}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg sm:text-xl font-bold text-light-on-surface dark:text-dark-on-surface flex items-center gap-2">
                <Users className="w-5 h-5 sm:w-6 sm:h-6 text-light-primary dark:text-dark-primary" />
                Lista Amici
              </h3>
              {recentFriends.length > 0 && (
                <button
                  onClick={() => navigate('/gestione-amici')}
                  className="text-sm font-medium text-light-primary dark:text-dark-primary hover:underline flex items-center gap-1"
                >
                  Vedi tutti
                  <ChevronRight className="w-4 h-4" />
                </button>
              )}
            </div>

            {recentFriends.length === 0 ? (
              <div className="text-center py-8 sm:py-12">
                <Users className="w-12 h-12 sm:w-16 sm:h-16 mx-auto mb-3 text-light-on-surface-variant dark:text-dark-on-surface-variant opacity-30" />
                <p className="text-sm sm:text-base text-light-on-surface-variant dark:text-dark-on-surface-variant">
                  Nessun amico registrato
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentFriends.map((friend) => (
                  <div
                    key={friend.id}
                    onClick={() => navigate('/gestione-amici')}
                    className="bg-light-surface dark:bg-dark-surface rounded-lg p-3 sm:p-4 cursor-pointer
                      hover:bg-light-surface-bright dark:hover:bg-dark-surface-bright transition-colors
                      flex items-center justify-between"
                  >
                    <div className="flex-1 min-w-0">
                      <p className="font-semibold text-light-on-surface dark:text-dark-on-surface">
                        {friend.firstName} {friend.lastName}
                      </p>
                      <p className="text-sm text-light-on-surface-variant dark:text-dark-on-surface-variant">
                        @{friend.username}
                      </p>
                    </div>
                    <span className="text-xs text-light-on-surface-variant dark:text-dark-on-surface-variant flex-shrink-0 ml-2">
                      {formatDate(friend.createdAt)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;